import {
  HttpClient,
  HttpErrorResponse,
  HttpEvent,
  HttpEventType,
  HttpResponse,
} from '@angular/common/http';
import { Service, inject } from '@angular/core';
import { sha256 } from '@noble/hashes/sha2.js';
import { Observable, Subscription, firstValueFrom } from 'rxjs';

/** The API is served under /api on the app's own origin (CloudFront router; dev server proxy). */
export const API = '/api';

export interface StampRecord {
  id: string;
  /** Empty when the name is encrypted. */
  fileName: string;
  /** The file name, encrypted in the browser with the user's key. */
  encryptedName: string | null;
  hash: string;
  /** `archived`: stored without a timestamp, until it is notarized from the Archive view. */
  status: 'archived' | 'pending' | 'confirmed';
  /** When the file was put in the archive, for stamps that started there. */
  archivedAt: string | null;
  /** A copy of the original file is in storage. */
  hasOriginal: boolean;
  /** That copy was encrypted in this browser (encryption is opt-in per user). */
  originalEncrypted: boolean;
  /** Its size in bytes, or null when there is none. */
  originalSize: number | null;
  /** The folder it is sorted into; null, or a folder that no longer exists, means the root. */
  folderId: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Timestamps and archived files are sorted in two separate folder trees. */
export type FolderSpace = 'timestamps' | 'archive';

/** A folder the user made to sort their files; folders nest through `parentId`. */
export interface Folder {
  id: string;
  name: string;
  space: FolderSpace;
  /** Null at the root. */
  parentId: string | null;
  createdAt: string;
  updatedAt: string;
}

/** One folder as the server lists it (GET /browse). */
export interface FolderPage {
  /** Files directly in the folder, newest first: one page. */
  files: StampRecord[];
  /** Where the next page starts, or null at the end. */
  next: string | null;
  /** Files directly in the folder, by status. */
  counts: { all: number; pending: number; confirmed: number };
  /** Files directly in each subfolder, by folder id. */
  folderCounts: Record<string, number>;
}

/** A presigned S3 POST: the form fields to send, then the file. */
export interface Upload {
  url: string;
  fields: Record<string, string>;
}

/** An S3 multipart upload: one presigned PUT per part of `partBytes`. */
export interface MultipartUpload {
  uploadId: string;
  partBytes: number;
  urls: string[];
}

/** Progress and cancellation of an upload or download of a file. */
export interface Transfer {
  /** Bytes sent (or received) so far, of `total`. */
  onProgress?: (done: number, total: number) => void;
  /** Aborting it stops the transfer, which then fails with an AbortError. */
  signal?: AbortSignal;
}

/** Above this, a plain original is downloaded by the browser itself, straight to disk. */
export const DIRECT_DOWNLOAD_ABOVE_BYTES = 256 * 1024 * 1024;

/** Largest original that can be stored (backend/files.ts MAX_ORIGINAL_BYTES). */
export const MAX_ORIGINAL_BYTES = 1024 * 1024 * 1024;
/** Larger files go up in parts, each retried on its own; smaller ones in one POST. */
const MULTIPART_ABOVE_BYTES = 100 * 1024 * 1024;
const PARALLEL_PARTS = 4;
const PART_ATTEMPTS = 3;

export interface VerifyResult {
  verified: boolean;
  matches: boolean;
  proofHash: string;
  message: string;
  bitcoin?: { height: number; time: string } | null;
  ots?: string;
  info?: string;
}

export type Stage = 'submitted' | 'mempool' | 'block' | 'confirmed';

export interface StampProgress {
  stage: Stage;
  submittedAt: string;
  estimatedAt: string | null;
  basis: string;
  txid: string | null;
  txUrl: string | null;
  blockHeight: number | null;
  blockTime: string | null;
  confirmations: number;
  requiredConfirmations: number;
  calendar: string | null;
  checkedAt: string;
}

/** Above this, a file is hashed in slices rather than read into memory whole. */
const HASH_IN_SLICES_ABOVE_BYTES = 256 * 1024 * 1024;
const HASH_SLICE_BYTES = 8 * 1024 * 1024;

/**
 * SHA-256 of a file, computed locally so the file never leaves the browser: with Web Crypto,
 * which needs the whole file in memory, or in slices for large files.
 */
export async function sha256Hex(
  file: Blob,
  onProgress?: (done: number, total: number) => void,
): Promise<string> {
  if (file.size > HASH_IN_SLICES_ABOVE_BYTES)
    return sha256HexInSlices(file, HASH_SLICE_BYTES, onProgress);
  const digest = await crypto.subtle.digest('SHA-256', new Uint8Array(await file.arrayBuffer()));
  return hex(new Uint8Array(digest));
}

/** The same digest as sha256Hex, holding one slice of the file in memory at a time. */
export async function sha256HexInSlices(
  file: Blob,
  sliceBytes = HASH_SLICE_BYTES,
  onProgress?: (done: number, total: number) => void,
) {
  const hash = sha256.create();
  for (let start = 0; start < file.size; start += sliceBytes) {
    hash.update(new Uint8Array(await file.slice(start, start + sliceBytes).arrayBuffer()));
    onProgress?.(Math.min(start + sliceBytes, file.size), file.size);
  }
  return hex(hash.digest());
}

const hex = (bytes: Uint8Array) =>
  Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');

export async function fileToBase64(file: Blob): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

@Service()
export class NotaryService {
  private readonly http = inject(HttpClient);

  list() {
    return firstValueFrom(this.http.get<StampRecord[]>(`${API}/stamps`));
  }

  /**
   * Sends only the digest and the file name: encrypted when encryption is on, else as is, and
   * the folder to put it in. The answer carries a presigned POST for uploading the original.
   */
  stamp(hash: string, encryptedName: string | null, fileName?: string, folderId?: string | null) {
    return firstValueFrom(
      this.http.post<StampRecord & { upload?: Upload }>(`${API}/stamps`, {
        hash,
        encryptedName,
        fileName,
        folderId,
      }),
    );
  }

  /** Like `stamp`, but only stores the file, in the archive: nothing is sent to Bitcoin. */
  archive(hash: string, encryptedName: string | null, fileName?: string, folderId?: string | null) {
    return firstValueFrom(
      this.http.post<StampRecord & { upload?: Upload }>(`${API}/archive`, {
        hash,
        encryptedName,
        fileName,
        folderId,
      }),
    );
  }

  /** Timestamps an archived file with the digest recorded when it was archived. */
  notarize(id: string) {
    return firstValueFrom(this.http.post<StampRecord>(`${API}/stamps/${id}/notarize`, {}));
  }

  /** A page of one folder (null: the tree's root); `status` keeps pending or confirmed only. */
  browse(
    space: FolderSpace,
    folderId: string | null,
    options: { status?: 'pending' | 'confirmed'; cursor?: string | null } = {},
  ) {
    const params: Record<string, string> = { space, folder: folderId ?? 'root' };
    if (options.status) params['status'] = options.status;
    if (options.cursor) params['cursor'] = options.cursor;
    return firstValueFrom(this.http.get<FolderPage>(`${API}/browse`, { params }));
  }

  folders() {
    return firstValueFrom(this.http.get<Folder[]>(`${API}/folders`));
  }

  createFolder(space: FolderSpace, name: string, parentId: string | null) {
    return firstValueFrom(this.http.post<Folder>(`${API}/folders`, { space, name, parentId }));
  }

  /** Renames a folder and/or moves it, with everything in it, under another (null: root). */
  updateFolder(id: string, fields: { name?: string; parentId?: string | null }) {
    return firstValueFrom(this.http.post<Folder>(`${API}/folders/${id}`, fields));
  }

  /** Deletes a folder; its files and subfolders move up to its parent, nothing is deleted. */
  deleteFolder(id: string) {
    return firstValueFrom(this.http.delete(`${API}/folders/${id}`));
  }

  /** Moves up to 100 stamps into a folder, or to the root with null. */
  moveStamps(ids: readonly string[], folderId: string | null) {
    return firstValueFrom(this.http.post<StampRecord[]>(`${API}/stamps/move`, { ids, folderId }));
  }

  setEncryptedName(id: string, encryptedName: string) {
    return firstValueFrom(
      this.http.post<StampRecord>(`${API}/stamps/${id}/name`, { encryptedName }),
    );
  }

  progress(id: string) {
    return firstValueFrom(this.http.get<StampProgress>(`${API}/stamps/${id}/progress`));
  }

  /** The progress of up to 50 stamps in one call; ids the server could not check are left out. */
  progressMany(ids: readonly string[]) {
    return firstValueFrom(
      this.http.post<Record<string, StampProgress>>(`${API}/progress`, { ids }),
    );
  }

  upgrade(id: string) {
    return firstValueFrom(this.http.post<StampRecord>(`${API}/stamps/${id}/upgrade`, {}));
  }

  verify(hash: string, ots: string) {
    return firstValueFrom(this.http.post<VerifyResult>(`${API}/verify`, { hash, ots }));
  }

  /**
   * Uploads the original (encrypted first when encryption is on) straight to S3 with a
   * presigned POST, valid for two minutes and limited by S3 to this stamp's key and 1 GB.
   * Files over 100 MB go up in parts instead. Reports progress and can be cancelled (`transfer`).
   */
  async uploadOriginal(id: string, file: Blob, upload?: Upload, transfer: Transfer = {}) {
    if (file.size > MAX_ORIGINAL_BYTES) throw new Error('Files over 1 GB cannot be stored.');
    if (file.size > MULTIPART_ABOVE_BYTES) return this.uploadInParts(id, file, transfer);
    const target =
      upload ??
      (await firstValueFrom(this.http.post<{ upload: Upload }>(`${API}/stamps/${id}/original`, {})))
        .upload;
    const form = new FormData();
    for (const [name, value] of Object.entries(target.fields)) form.append(name, value);
    form.append('file', file);
    await run(
      this.http.post(target.url, form, {
        responseType: 'text',
        reportProgress: true,
        observe: 'events',
      }),
      {
        signal: transfer.signal,
        // The form fields add a few bytes: progress is the file's.
        onProgress: (done) => transfer.onProgress?.(Math.min(done, file.size), file.size),
      },
    );
    return this.completeOriginal(id);
  }

  /**
   * Sends the parts a few at a time, retrying each on a network or server error, then has the
   * server assemble and check them. On failure or cancellation the parts still in flight stop
   * and the upload is aborted (S3 also drops it after a day).
   */
  private async uploadInParts(id: string, file: Blob, transfer: Transfer): Promise<StampRecord> {
    const { multipart } = await firstValueFrom(
      this.http.post<{ multipart: MultipartUpload }>(`${API}/stamps/${id}/original/multipart`, {
        size: file.size,
      }),
    );
    const { uploadId, partBytes, urls } = multipart;
    // Stops every part, on the caller's cancel or on the first part that fails.
    const stop = new AbortController();
    const cancel = () => stop.abort();
    transfer.signal?.addEventListener('abort', cancel);
    const sent = urls.map(() => 0);
    const report = () =>
      transfer.onProgress?.(
        sent.reduce((a, b) => a + b, 0),
        file.size,
      );
    let next = 0;
    const sendParts = async () => {
      while (!stop.signal.aborted && next < urls.length) {
        const i = next++;
        const part = file.slice(i * partBytes, (i + 1) * partBytes);
        await retried(() => {
          // A retried part starts again from zero.
          sent[i] = 0;
          report();
          return run(
            this.http.put(urls[i], part, {
              responseType: 'text',
              reportProgress: true,
              observe: 'events',
            }),
            {
              signal: stop.signal,
              onProgress: (done) => {
                sent[i] = Math.min(done, part.size);
                report();
              },
            },
          );
        });
        sent[i] = part.size;
        report();
      }
    };
    try {
      await Promise.all(
        Array.from({ length: Math.min(PARALLEL_PARTS, urls.length) }, () =>
          sendParts().catch((err: unknown) => {
            stop.abort();
            throw err;
          }),
        ),
      );
      if (stop.signal.aborted) throw abortError();
    } catch (err) {
      void firstValueFrom(
        this.http.post(`${API}/stamps/${id}/original/multipart/abort`, { uploadId }),
      ).catch(() => {});
      throw transfer.signal?.aborted ? abortError() : err;
    } finally {
      transfer.signal?.removeEventListener('abort', cancel);
    }
    return firstValueFrom(
      this.http.post<StampRecord>(`${API}/stamps/${id}/original/multipart/complete`, {
        uploadId,
      }),
    );
  }

  /**
   * Has the server read the stored original's size and whether it is encrypted. Also repairs a
   * record whose upload went through but was never completed (e.g. a sign-out in between).
   */
  completeOriginal(id: string) {
    return firstValueFrom(this.http.post<StampRecord>(`${API}/stamps/${id}/original/complete`, {}));
  }

  /** The stored original, still to be decrypted in the browser when it was encrypted. */
  async fetchOriginal(id: string, transfer: Transfer = {}): Promise<Blob> {
    const downloadUrl = await this.originalDownloadUrl(id);
    return run(
      this.http.get(downloadUrl, {
        responseType: 'blob',
        reportProgress: true,
        observe: 'events',
      }),
      transfer,
    );
  }

  /**
   * A short-lived link to the stored original. With `fileName`, S3 serves it as an attachment of
   * that name: opened, the browser saves it straight to disk, with its own progress.
   */
  async originalDownloadUrl(id: string, fileName?: string): Promise<string> {
    const params: Record<string, string> = fileName ? { name: fileName } : {};
    const { downloadUrl } = await firstValueFrom(
      this.http.get<{ downloadUrl: string }>(`${API}/stamps/${id}/original`, { params }),
    );
    return downloadUrl;
  }

  /** Downloads go through HttpClient so the request carries the session token. */
  async download(id: string, kind: 'ots' | 'certificate', fileName?: string) {
    const url = `${API}/stamps/${id}/${kind}`;
    const options = { responseType: 'blob' as const, observe: 'response' as const };
    // The certificate prints the decrypted name, which the server uses for this response only.
    const res = await firstValueFrom(
      kind === 'certificate'
        ? this.http.post(url, { fileName }, options)
        : this.http.get(url, options),
    );
    const fallback = `${id}.${kind === 'ots' ? 'ots' : 'pdf'}`;
    const safe = fileName?.replace(/[^\w.-]/g, '_');
    const name =
      kind === 'ots' && safe
        ? `${safe}.ots`
        : (/filename="([^"]+)"/.exec(res.headers.get('Content-Disposition') ?? '')?.[1] ??
          fallback);
    saveBlob(res.body!, name);
  }
}

/**
 * The response of an HttpClient request made with `observe: 'events'`, reporting its upload or
 * download progress. Aborting `signal` cancels the request (the XHR is aborted).
 */
function run<T>(events: Observable<HttpEvent<T>>, transfer: Transfer = {}): Promise<T> {
  const { signal, onProgress } = transfer;
  return new Promise<T>((resolve, reject) => {
    if (signal?.aborted) {
      reject(abortError());
      return;
    }
    let subscription: Subscription | undefined;
    const cancel = () => {
      subscription?.unsubscribe();
      reject(abortError());
    };
    signal?.addEventListener('abort', cancel, { once: true });
    const done = () => signal?.removeEventListener('abort', cancel);
    subscription = events.subscribe({
      next: (event) => {
        if (
          event.type === HttpEventType.UploadProgress ||
          event.type === HttpEventType.DownloadProgress
        )
          onProgress?.(event.loaded, event.total ?? event.loaded);
        else if (event instanceof HttpResponse) resolve(event.body as T);
      },
      error: (err: unknown) => {
        done();
        reject(err);
      },
      complete: done,
    });
  });
}

const abortError = () => new DOMException('The transfer was cancelled.', 'AbortError');

/** Whether `err` is a cancelled transfer (see Transfer.signal). */
export const isAbort = (err: unknown) => err instanceof DOMException && err.name === 'AbortError';

/** Runs `send` again after a network or server error, waiting a little longer each time. */
async function retried<T>(send: () => Promise<T>): Promise<T> {
  for (let attempt = 1; ; attempt++) {
    try {
      return await send();
    } catch (err) {
      const transient = err instanceof HttpErrorResponse && (err.status === 0 || err.status >= 500);
      if (!transient || attempt >= PART_ATTEMPTS) throw err;
      await new Promise((resolve) => setTimeout(resolve, 1000 * 2 ** attempt));
    }
  }
}

export function saveBlob(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
